import { Button } from '@/components/ui/button';
import { useCartStore } from '@/store/cartStore';
import { useTranslation } from 'react-i18next';
import { OrderReviewItem } from './OrderReviewItem';

interface OrderSummaryProps {
  onEdit: () => void;
  onConfirm: () => void;
  isSubmitting?: boolean;
}

export function OrderSummary({ onEdit, onConfirm, isSubmitting = false }: OrderSummaryProps) {
  const { i18n } = useTranslation('customer');
  const { items, total } = useCartStore();
  
  const subtotalEGP = (total / 100).toFixed(1);
  const totalEGP = (total / 100).toFixed(1); // Same as subtotal for MVP (no tax/fees)
  const isRTL = i18n.language === 'ar';

  return (
    <div className="w-full max-w-2xl mx-auto" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-center">
          {isRTL ? 'ملخص الطلب' : 'Order Summary'}
        </h1>
      </div>

      {/* Items List */}
      <div className="bg-card rounded-lg border p-4 mb-6">
        {(() => {
          const validItems = items.filter(item => item.quantity > 0);
          return validItems.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              {isRTL ? 'لا توجد عناصر في الطلب' : 'No items in order'}
            </p>
          ) : (
            <div role="list" aria-label={isRTL ? 'عناصر الطلب' : 'Order items'}>
              {validItems.map((item) => (
                <OrderReviewItem key={item.id} item={item} />
              ))}
            </div>
          );
        })()}
      </div>

      {/* Totals */}
      <div className="bg-card rounded-lg border p-4 mb-6 space-y-2">
        <div className="flex justify-between items-center">
          <span className="text-muted-foreground">
            {isRTL ? 'المجموع الفرعي' : 'Subtotal'}
          </span>
          <span className="font-medium">{subtotalEGP} EGP</span>
        </div>
        <div className="border-t pt-2 flex justify-between items-center">
          <span className="text-lg font-semibold">
            {isRTL ? 'الإجمالي' : 'Total'}
          </span>
          <span className="text-xl font-bold">{totalEGP} EGP</span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className={`flex gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <Button
          variant="outline"
          className="flex-1"
          onClick={onEdit}
          disabled={isSubmitting}
          aria-label={isRTL ? 'تعديل السلة' : 'Edit Cart'}
        >
          {isRTL ? 'تعديل السلة' : 'Edit Cart'}
        </Button>
        <Button
          className="flex-1"
          onClick={onConfirm}
          disabled={isSubmitting || items.filter(item => item.quantity > 0).length === 0}
          aria-label={isRTL ? 'تأكيد الطلب' : 'Confirm Order'}
        >
          {isSubmitting 
            ? (isRTL ? 'جاري التأكيد...' : 'Confirming...')
            : (isRTL ? 'تأكيد الطلب' : 'Confirm Order')
          }
        </Button>
      </div>
    </div>
  );
}








